import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';

export interface SubmitSupportReportPayload {
  chatId: string;
  reportedUid: string;
  category: string;
  details: string;
  screenshots: string[];
}

export interface SubmitSupportReportResponse {
  success: boolean;
  code: string;
  report_id: string;
  message: string;
}

export interface BlockClientPayload {
  clientUid: string;
  chatId?: string;
  reason?: string;
}

export interface BlockClientResponse {
  success: boolean;
  code: string;
  message: string;
}

const getAuthToken = async (): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User is not authenticated');
  return await currentUser.getIdToken(true);
};

export const submitSupportReportApi = async (
  payload: SubmitSupportReportPayload,
): Promise<SubmitSupportReportResponse> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SUBMIT_SUPPORT_REPORT}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chat_id: payload.chatId,
      reported_uid: payload.reportedUid,
      category: payload.category,
      details: payload.details,
      screenshots: payload.screenshots,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to submit report: ${response.status}`);
  }

  return response.json();
};

export const useSubmitSupportReportMutation = () => {
  return useMutation({
    mutationFn: submitSupportReportApi,
  });
};

export const blockClientApi = async (payload: BlockClientPayload): Promise<BlockClientResponse> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_BLOCK_CLIENT}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_uid: payload.clientUid,
      chat_id: payload.chatId,
      reason: payload.reason,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to block client: ${response.status}`);
  }

  return response.json();
};

export const useBlockClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: blockClientApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-clients'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-messages-page'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-program-plan-list'] });
    },
  });
};

export interface UnblockClientPayload {
  clientUid: string;
}

export interface UnblockClientResponse {
  success: boolean;
  code: string;
  message: string;
}

export const unblockClientApi = async (
  payload: UnblockClientPayload,
): Promise<UnblockClientResponse> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UNBLOCK_CLIENT}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_uid: payload.clientUid,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to unblock client: ${response.status}`);
  }

  return response.json();
};

export const useUnblockClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unblockClientApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-clients'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-messages-page'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-program-plan-list'] });
    },
  });
};
